<template>
  <div id="app" :class="{'overflow-hidden' : overflowHidden}">
    <app-header v-if="!isHeadless && !(isMobile || isTablet)" />
    <mobile-menu v-if="(isMobile || isTablet) && isLoggedIn" />
    <side-panel v-if="!(isMobile || isTablet)"/>
    <router-view />
    <app-footer v-if="false && !isFootless" />

    <loader />
    <flash-message />
    <phone-controller />

    <session-expired-modal />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import $ from 'jquery';
import eventBus from '@/eventBus';

import AppHeader from '@/components/Header';
import MobileMenu from '@/components/MobileMenu';
import AppFooter from '@/components/Footer';
import Loader from '@/components/Loader';
import SidePanel from '@/components/SidePanel';
import FlashMessage from '@/components/FlashMessage';
import SessionExpiredModal from '@/components/SessionExpiredModal';
import PhoneController from '@/components/PhoneController';

import { PATHS } from '@/consts';

const { useState: useUserState, useGetters: useUserGetters } = createNamespacedHelpers('user');
const { useState: useAppState, useActions: useAppActions } = createNamespacedHelpers('app');

export default {
  name: 'App',
  components: {
    AppHeader,
    MobileMenu,
    AppFooter,
    Loader,
    FlashMessage,
    SidePanel,
    SessionExpiredModal,
    PhoneController
  },
  metaInfo() {
    const { $i18n } = this;

    return {
      titleTemplate: `%s | ${$i18n.t('SITENAME')}`,
      htmlAttrs: {
        lang: this.locale,
      },
      meta: []
    };
  },
  setup(props, { root }) {
    const route = ref(root.$route);
    const isHeadless = computed(() => route.value.meta.headless);
    const isFootless = computed(() => route.value.meta.footless);
    const overflowHidden = ref(false);

    const { profile } = useUserState(['profile']);
    const { isMobile, isTablet } = useAppState(['isMobile', 'isTablet']);
    const { username } = profile.value;

    const { getMisc, setScreenSize } = useAppActions(['getMisc', 'setScreenSize']);
    const { isLoggedIn } = useUserGetters(['isLoggedIn']);

    const watchScreenSize = () => {
      if (process.browser) {
        const w = window.innerWidth;
        setScreenSize(w);
      }
    };

    watch(
      () => root.$route,
      (r) => {
        route.value = r;
      }
    );

    watch(profile, (val) => {
      if (val.username) {
        const OneSignal = window.OneSignal || [];

        OneSignal.push(() => {
          OneSignal.sendTag('username', val.username);
        });
      }
    });

    watch(isLoggedIn, (val) => {
      if (!val) {
        const { requiresAuth } = root.$route.meta;
        if (requiresAuth) {
          root.$router.push(PATHS.LOGIN);
        }
      }
    });

    onMounted(() => {
      $(window).on('resize', watchScreenSize);
      watchScreenSize();
      getMisc();

      if (process.browser) {
        if (!process.env.VUE_APP_ONESIGNAL_APP_ID) return;

        const OneSignal = window.OneSignal || [];
        const initConfig = {
          appId: process.env.VUE_APP_ONESIGNAL_APP_ID,
          safari_web_id: process.env.VUE_APP_ONESIGNAL_SAFARI_WEB_ID,
          allowLocalhostAsSecureOrigin: !!process.env.VUE_APP_DEBUG,
          notifyButton: {
            enable: true,
            displayPredicate() {
              return OneSignal.isPushNotificationsEnabled().then((isPushEnabled) => !isPushEnabled);
            },
            text: {
              'tip.state.unsubscribed': root.$i18n.t('SUBSCRIBE_TO_NOTIFICATIONS'),
              'tip.state.subscribed': root.$i18n.t('YOURE_SUBSCRIBED_TO_NOTIFICATIONS'),
              'tip.state.blocked': root.$i18n.t('YOUVE_BLOCKED_NOTIFICATIONS'),
              'message.prenotify': root.$i18n.t('CLICK_TO_SUBSCRIBE_TO_NOTIFICATIONS')
            },
            colors: {
              'circle.background': '#DE1E25',
              'circle.foreground': '#FFFFFC',
              'badge.background': '#DE1E25',
              'badge.foreground': '#FFFFFC',
              'badge.bordercolor': '#FFFFFC',
              'pulse.color': '#FFFFFC'
            },
          },
          welcomeNotification: {
            title: 'Camdonhanh CRM',
            message: root.$i18n.t('SUBSCRIBED_TO_CAMDONHANH_CRM_NOTIFICATIONS'),
          },
          promptOptions: {
            slidedown: {
              enabled: true,
              autoPrompt: true,
              actionMessage: root.$i18n.t('SUBSCRIBE_TO_CAMDONHANH_CRM_NOTIFICATIONS'),
              acceptButtonText: root.$i18n.t('ACCEPT'),
              cancelButtonText: root.$i18n.t('DENY')
            }
          }
        };

        if (process.env.VUE_APP_DEBUG) {
          OneSignal.log.setLevel('trace');
        }

        OneSignal.push(() => {
          OneSignal.init(initConfig);

          if (username) {
            OneSignal.sendTag('username', username);
          }
        });
      }

      eventBus.$on('body-overflow-hidden', (value) => {
        overflowHidden.value = value;
        if (value) {
          document.body.classList.add('overflow-hidden');
        } else {
          document.body.classList.remove('overflow-hidden');
        }
      });
    });

    onUnmounted(() => {
      $(window).off('resize', watchScreenSize);
    });

    return {
      isHeadless,
      isFootless,
      overflowHidden,
      isMobile,
      isTablet,
      isLoggedIn,
    };
  }
};
</script>

<style lang="scss">
@import '~@/assets/scss/fonts';
@import '~@/assets/scss/colors';
@import '~@/assets/scss/dimens';

body {
  font-family: "Source Sans Pro", Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: $ghostWhite;
  color: $independence;
  margin: 0;
  overflow-x: hidden;
  min-height: 100vh;

  padding-top: $headerHeight;
  padding-left: $sidePanelWidth;

  @media (max-width: 991px) {
    padding-left: 0px;
  }
}

[cloak] { display:none !important; }
</style>
